import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import AdminOverviewTab from "../components/admin/AdminOverviewTab";
import AdminOpsTab from "../components/admin/AdminOpsTab";
import AdminUsersTab from "../components/admin/AdminUsersTab";
import AdminBotsTab from "../components/admin/AdminBotsTab";
import AdminTreasuryTab from "../components/admin/AdminTreasuryTab";

export interface AdminUser {
    id: string;
    username: string;
    createdAt: string;
    maxBots: number;
    maxBotsOverride: number | null;
    tierMaxBots: number;
    isAdmin: boolean;
    tier: "pro" | "free";
    walletAddress?: string;
    botCount: number;
    lastLoginAt?: string;
    lastSeenAt?: string;
}

export interface AdminBot {
    instanceId: string;
    poolId: string;
    poolType?: string;
    methods: Record<string, { isRunning: boolean; params: Record<string, unknown> }>;
    feesCollected?: number;
    runningTotals?: {
        volume?: { total: string; updatedAt?: string };
        inject?: { total: string; updatedAt?: string };
        extract?: { total: string; updatedAt?: string };
        defendFloor?: { total: string; updatedAt?: string };
    };
    userId?: {
        _id?: string;
        username?: string;
        tier?: "pro" | "free";
        isAdmin?: boolean;
        maxBots?: number;
        walletAddress?: string;
    };
}

export interface PaginatedResponse<T> {
    items: T[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

export type BotMethodType = "volume" | "inject" | "extract" | "defendFloor";

export interface OverviewResponse {
    range: {
        key: "24h" | "7d" | "30d" | "90d";
        start: string;
        end: string;
    };
    kpis: {
        totalUsers: number;
        activeUsers: number;
        newUsers: number;
        totalBots: number;
        activeBots: number;
        newBots: number;
        totalFeesLamports: number;
        totalFeesSol: number;
        methodRunsInRange: number;
    };
    methods: {
        usage: {
            methodType: BotMethodType;
            runCount: number;
            completedCount: number;
            totalValue: number;
            avgDurationMs: number;
        }[];
        activeNow: Record<BotMethodType, number>;
    };
    series: {
        daily: {
            day: string;
            runs: number;
            totalValue: number;
            methods: Record<BotMethodType, { runs: number; totalValue: number }>;
        }[];
    };
}

export interface TreasuryToken {
    mint: string;
    symbol: string;
    amount: number;
}

export interface TreasuryWallet {
    address: string;
    sol: number;
    tokens: TreasuryToken[];
}

export interface TreasuryResponse {
    asOf: string;
    totalSol: number;
    wallets: TreasuryWallet[];
    snapshot: {
        snapshotAt: string;
        totalSol: number;
    } | null;
}

export interface TreasuryHistoryResponse {
    items: {
        snapshotAt: string;
        totalSol: number;
    }[];
    total: number;
}

export interface OpsResponse {
    range: {
        key: "24h" | "7d" | "30d" | "90d";
        start: string;
        end: string;
    };
    generatedAt: string;
    sampleWindow: {
        apiStart: string;
        rpcStart: string;
    };
    server: {
        requests: {
            total: number;
            errors: number;
            errorRate: number;
            perMinute: number;
            avgLatencyMs: number;
            p95LatencyMs: number;
        };
        topRoutes: {
            method: string;
            route: string;
            count: number;
            errors: number;
            errorRate: number;
            avgLatencyMs: number;
            p95LatencyMs: number;
        }[];
    };
    rpc: {
        endpointHost: string | null;
        calls: {
            total: number;
            failed: number;
            failureRate: number;
            perMinute: number;
            avgLatencyMs: number;
            p95LatencyMs: number;
        };
        health: {
            ok: boolean;
            latencyMs: number | null;
            error: string | null;
        };
        topMethods: {
            method: string;
            count: number;
            failed: number;
            failureRate: number;
            avgLatencyMs: number;
            p95LatencyMs: number;
        }[];
    };
    pressure: {
        runningBots: number;
        activeMethods: Record<BotMethodType, number>;
        estimatedLoopsPerMinute: number;
        estimatedRpcRpm: number;
        rpcSoftLimitRpm: number | null;
        utilizationPct: number | null;
    };
    quality: {
        methodRuns: {
            total: number;
            completed: number;
            errored: number;
            stopped: number;
            errorRate: number;
        };
    };
    thresholds: {
        apiErrorWarnPct: number;
        apiErrorCriticalPct: number;
        rpcFailureWarnPct: number;
        rpcFailureCriticalPct: number;
        rpcHealthWarnLatencyMs: number;
        rpcUtilizationWarnPct: number;
        rpcUtilizationCriticalPct: number;
        methodErrorWarnPct: number;
        methodErrorCriticalPct: number;
        methodErrorMinRuns: number;
    };
    alerts: {
        severity: "info" | "warn" | "critical";
        title: string;
        message: string;
    }[];
}


const AdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"overview" | "ops" | "users" | "bots" | "treasury">("overview");

    const [overviewRange, setOverviewRange] = useState<"24h" | "7d" | "30d" | "90d">("30d");
    const [overview, setOverview] = useState<OverviewResponse | null>(null);
    const [loadingOverview, setLoadingOverview] = useState(false);
    const [opsRange, setOpsRange] = useState<"24h" | "7d" | "30d" | "90d">("24h");
    const [ops, setOps] = useState<OpsResponse | null>(null);
    const [loadingOps, setLoadingOps] = useState(false);

    const [users, setUsers] = useState<AdminUser[]>([]);
    const [usersTotal, setUsersTotal] = useState(0);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [usersPage, setUsersPage] = useState(1);
    const [usersPageSize, setUsersPageSize] = useState(25);
    const [usersSearch, setUsersSearch] = useState("");
    const [usersTier, setUsersTier] = useState<"all" | "pro" | "free">("all");
    const [usersRole, setUsersRole] = useState<"all" | "admin" | "user">("all");
    const [usersSortBy, setUsersSortBy] = useState<"createdAt" | "username" | "tier" | "lastSeenAt">("createdAt");
    const [usersSortDir, setUsersSortDir] = useState<"asc" | "desc">("desc");
    const [maxBotsDraft, setMaxBotsDraft] = useState<Record<string, string>>({});

    const [bots, setBots] = useState<AdminBot[]>([]);
    const [botsTotal, setBotsTotal] = useState(0);
    const [loadingBots, setLoadingBots] = useState(false);
    const [botsPage, setBotsPage] = useState(1);
    const [botsPageSize, setBotsPageSize] = useState(25);
    const [botsSearch, setBotsSearch] = useState("");
    const [botsPoolType, setBotsPoolType] = useState<string>("all");
    const [botsMethod, setBotsMethod] = useState<string>("all");
    const [botsRunningOnly, setBotsRunningOnly] = useState(false);
    const [botsSortBy, setBotsSortBy] = useState<"createdAt" | "instanceId" | "feesCollected" | "user">("createdAt");
    const [botsSortDir, setBotsSortDir] = useState<"asc" | "desc">("desc");
    const [isCompactLayout, setIsCompactLayout] = useState(() =>
        typeof window !== "undefined" ? window.innerWidth < 1100 : false,
    );

    const [treasury, setTreasury] = useState<TreasuryResponse | null>(null);
    const [loadingTreasury, setLoadingTreasury] = useState(false);
    const [treasuryHistory, setTreasuryHistory] = useState<TreasuryHistoryResponse | null>(null);
    const [loadingTreasuryHistory, setLoadingTreasuryHistory] = useState(false);

    const usersTotalPages = useMemo(() => Math.max(1, Math.ceil(usersTotal / usersPageSize)), [usersTotal, usersPageSize]);
    const botsTotalPages = useMemo(() => Math.max(1, Math.ceil(botsTotal / botsPageSize)), [botsTotal, botsPageSize]);
    const debouncedUsersSearch = useDebouncedValue(usersSearch, 350);
    const debouncedBotsSearch = useDebouncedValue(botsSearch, 350);

    useEffect(() => {
        const onResize = () => setIsCompactLayout(window.innerWidth < 1100);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const fetchOverview = useCallback(async () => {
        setLoadingOverview(true);
        setError(null);
        try {
            const res = await axios.get<OverviewResponse>("/api/admin/overview", {
                params: { range: overviewRange },
            });
            setOverview(res.data);
        } catch (err) {
            console.error("Failed to fetch admin overview:", err);
            setError("Failed to load overview metrics");
        } finally {
            setLoadingOverview(false);
        }
    }, [overviewRange]);

    const fetchOps = useCallback(async () => {
        setLoadingOps(true);
        setError(null);
        try {
            const res = await axios.get<OpsResponse>("/api/admin/ops", {
                params: { range: opsRange },
            });
            setOps(res.data);
        } catch (err) {
            console.error("Failed to fetch ops metrics:", err);
            setError("Failed to load ops metrics");
        } finally {
            setLoadingOps(false);
        }
    }, [opsRange]);

    const fetchUsers = useCallback(async () => {
        setLoadingUsers(true);
        setError(null);
        try {
            const res = await axios.get<PaginatedResponse<AdminUser> | AdminUser[]>("/api/admin/users", {
                params: {
                    page: usersPage,
                    pageSize: usersPageSize,
                    search: debouncedUsersSearch,
                    tier: usersTier,
                    role: usersRole,
                    sortBy: usersSortBy,
                    sortDir: usersSortDir,
                },
            });

            if (Array.isArray(res.data)) {
                setUsers(res.data);
                setUsersTotal(res.data.length);
            } else {
                setUsers(res.data.items || []);
                setUsersTotal(res.data.total || 0);
            }
        } catch (err) {
            console.error("Failed to fetch users:", err);
            setError("Failed to load users");
        } finally {
            setLoadingUsers(false);
        }
    }, [debouncedUsersSearch, usersPage, usersPageSize, usersRole, usersSortBy, usersSortDir, usersTier]);

    const fetchBots = useCallback(async () => {
        setLoadingBots(true);
        setError(null);
        try {
            const res = await axios.get<PaginatedResponse<AdminBot> | AdminBot[]>("/api/admin/bots", {
                params: {
                    page: botsPage,
                    pageSize: botsPageSize,
                    search: debouncedBotsSearch,
                    poolType: botsPoolType,
                    method: botsMethod,
                    runningOnly: botsRunningOnly,
                    sortBy: botsSortBy,
                    sortDir: botsSortDir,
                },
            });

            if (Array.isArray(res.data)) {
                setBots(res.data);
                setBotsTotal(res.data.length);
            } else {
                setBots(res.data.items || []);
                setBotsTotal(res.data.total || 0);
            }
        } catch (err) {
            console.error("Failed to fetch bots:", err);
            setError("Failed to load bots");
        } finally {
            setLoadingBots(false);
        }
    }, [botsMethod, botsPage, botsPageSize, botsPoolType, botsRunningOnly, botsSortBy, botsSortDir, debouncedBotsSearch]);

    const fetchTreasury = useCallback(async (refresh = false) => {
        setLoadingTreasury(true);
        setError(null);
        try {
            const res = await axios.get<TreasuryResponse>("/api/admin/treasury", {
                params: refresh ? { refresh: true } : undefined,
            });
            setTreasury(res.data);
        } catch (err) {
            console.error("Failed to fetch treasury balances:", err);
            setError("Failed to load treasury balances");
        } finally {
            setLoadingTreasury(false);
        }
    }, []);

    const fetchTreasuryHistory = useCallback(async () => {
        setLoadingTreasuryHistory(true);
        setError(null);
        try {
            const res = await axios.get<TreasuryHistoryResponse>("/api/admin/treasury/history", {
                params: { limit: 96 },
            });
            setTreasuryHistory(res.data);
        } catch (err) {
            console.error("Failed to fetch treasury history:", err);
            setError("Failed to load treasury history");
        } finally {
            setLoadingTreasuryHistory(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === "overview") {
            fetchOverview();
        }
    }, [activeTab, fetchOverview]);

    useEffect(() => {
        if (activeTab === "ops") {
            fetchOps();
        }
    }, [activeTab, fetchOps]);

    useEffect(() => {
        if (activeTab === "users") {
            fetchUsers();
        }
    }, [activeTab, fetchUsers]);

    useEffect(() => {
        if (activeTab === "bots") {
            fetchBots();
        }
    }, [activeTab, fetchBots]);

    useEffect(() => {
        if (activeTab === "treasury") {
            void fetchTreasury(false);
            void fetchTreasuryHistory();
        }
    }, [activeTab, fetchTreasury, fetchTreasuryHistory]);

    useEffect(() => {
        setUsersPage(1);
    }, [debouncedUsersSearch, usersTier, usersRole, usersSortBy, usersSortDir, usersPageSize]);

    useEffect(() => {
        setBotsPage(1);
    }, [debouncedBotsSearch, botsPoolType, botsMethod, botsRunningOnly, botsSortBy, botsSortDir, botsPageSize]);

    useEffect(() => {
        setMaxBotsDraft((prev) => {
            const next = { ...prev };
            users.forEach((user) => {
                if (next[user.id] === undefined) {
                    next[user.id] = user.maxBotsOverride == null ? "" : String(user.maxBotsOverride);
                }
            });
            return next;
        });
    }, [users]);

    const updateUser = async (userId: string, updates: Record<string, unknown>) => {
        try {
            await axios.patch(`/api/admin/users/${userId}`, updates);
            await fetchUsers();
        } catch (err) {
            console.error("Failed to update user:", err);
            setError("Failed to update user");
        }
    };

    const saveMaxBotsOverride = async (user: AdminUser) => {
        const raw = (maxBotsDraft[user.id] ?? "").trim();
        const parsed = raw === "" ? null : Number(raw);
        if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) {
            setError("Max bots override must be empty or a non-negative number");
            return;
        }
        await updateUser(user.id, { maxBots: parsed });
    };

    const stopBot = async (instanceId: string) => {
        try {
            await axios.post(`/api/admin/bots/${instanceId}/stop`);
            await fetchBots();
        } catch (err) {
            console.error("Failed to stop bot:", err);
            setError("Failed to stop bot");
        }
    };

    return (
        <div className="sovereign-page-shell" style={{ minHeight: "100vh", padding: "24px", color: "white" }}>
            <div>
            <div className="sovereign-page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <div>
                    <div className="sovereign-brand" style={{ marginBottom: "10px" }}>
                        <img src="/logo1.png" alt="Stellaris" style={{ width: "26px", height: "26px", opacity: 0.95 }} />
                        <span className="sovereign-wordmark" style={{ fontSize: "18px", fontWeight: 700 }}>Stellaris</span>
                    </div>
                    <h1 className="sovereign-page-title" style={{ fontSize: "28px", fontWeight: 700, marginBottom: "6px" }}>Admin Dashboard</h1>
                    <p className="sovereign-page-subtitle" style={{ color: "#9ca3af", fontSize: "14px" }}>Scalable controls, metrics, and treasury visibility</p>
                </div>
                <button
                    onClick={() => navigate("/dashboard")}
                    className="sovereign-nav-button sovereign-nav-button--accent"
                    style={{ cursor: "pointer" }}
                >
                    Back to Dashboard
                </button>
            </div>

            <div className="sovereign-tab-row" style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
                {[
                    { id: "overview", label: "Overview" },
                    { id: "ops", label: "Ops & Limits" },
                    { id: "users", label: "Users" },
                    { id: "bots", label: "Bots" },
                    { id: "treasury", label: "Treasury" },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        className={`sovereign-tab-button${activeTab === tab.id ? " sovereign-tab-button--active" : ""}`}
                        onClick={() => setActiveTab(tab.id as "overview" | "ops" | "users" | "bots" | "treasury")}
                        style={{
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1px solid rgba(255,255,255,0.1)",
                            background: activeTab === tab.id ? "rgba(20, 241, 149, 0.2)" : "rgba(255,255,255,0.05)",
                            color: activeTab === tab.id ? "#14f195" : "white",
                            cursor: "pointer",
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {error && <p style={{ color: "#ff6b6b", marginBottom: "12px" }}>{error}</p>}

            {activeTab === "overview" && (
                <AdminOverviewTab
                    overview={overview}
                    overviewRange={overviewRange}
                    setOverviewRange={setOverviewRange}
                    loadingOverview={loadingOverview}
                    fetchOverview={fetchOverview}
                />
            )}

            {activeTab === "ops" && (
                <AdminOpsTab
                    ops={ops}
                    opsRange={opsRange}
                    setOpsRange={setOpsRange}
                    loadingOps={loadingOps}
                    fetchOps={fetchOps}
                />
            )}

            {activeTab === "users" && (
                <AdminUsersTab
                    users={users}
                    usersTotal={usersTotal}
                    loadingUsers={loadingUsers}
                    usersPage={usersPage}
                    setUsersPage={setUsersPage}
                    usersPageSize={usersPageSize}
                    setUsersPageSize={setUsersPageSize}
                    usersSearch={usersSearch}
                    setUsersSearch={setUsersSearch}
                    usersTier={usersTier}
                    setUsersTier={setUsersTier}
                    usersRole={usersRole}
                    setUsersRole={setUsersRole}
                    usersSortBy={usersSortBy}
                    setUsersSortBy={setUsersSortBy}
                    usersSortDir={usersSortDir}
                    setUsersSortDir={setUsersSortDir}
                    usersTotalPages={usersTotalPages}
                    maxBotsDraft={maxBotsDraft}
                    setMaxBotsDraft={setMaxBotsDraft}
                    updateUser={updateUser}
                    saveMaxBotsOverride={saveMaxBotsOverride}
                    isCompactLayout={isCompactLayout}
                />
            )}

            {activeTab === "bots" && (
                <AdminBotsTab
                    bots={bots}
                    botsTotal={botsTotal}
                    loadingBots={loadingBots}
                    botsPage={botsPage}
                    setBotsPage={setBotsPage}
                    botsPageSize={botsPageSize}
                    setBotsPageSize={setBotsPageSize}
                    botsSearch={botsSearch}
                    setBotsSearch={setBotsSearch}
                    botsPoolType={botsPoolType}
                    setBotsPoolType={setBotsPoolType}
                    botsMethod={botsMethod}
                    setBotsMethod={setBotsMethod}
                    botsRunningOnly={botsRunningOnly}
                    setBotsRunningOnly={setBotsRunningOnly}
                    botsSortBy={botsSortBy}
                    setBotsSortBy={setBotsSortBy}
                    botsSortDir={botsSortDir}
                    setBotsSortDir={setBotsSortDir}
                    botsTotalPages={botsTotalPages}
                    stopBot={stopBot}
                    isCompactLayout={isCompactLayout}
                />
            )}

            {activeTab === "treasury" && (
                <AdminTreasuryTab
                    treasury={treasury}
                    treasuryHistory={treasuryHistory}
                    loadingTreasury={loadingTreasury}
                    loadingTreasuryHistory={loadingTreasuryHistory}
                    fetchTreasury={fetchTreasury}
                    fetchTreasuryHistory={fetchTreasuryHistory}
                />
            )}
            </div>
        </div>
    );
};

export default AdminDashboard;
